import { useState, useCallback, useEffect } from "@wordpress/element";

export const AI_PROVIDERS = {
	openai: {
		name: "OpenAI",
		endpoint: "https://api.openai.com/v1/chat/completions",
	},
	anthropic: {
		name: "Anthropic",
		endpoint: "https://api.anthropic.com/v1/complete",
	},
	openrouter: {
		name: "OpenRouter",
		endpoint: "https://openrouter.ai/api/v1/chat/completions",
	},
};

const DEFAULT_MODEL = "openai:gpt-4o";

export default function useAiSuggestions() {
	const [apiKey, setApiKey] = useState("");
	const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
	const [showApiKeyInput, setShowApiKeyInput] = useState(false);
	const [tempApiKey, setTempApiKey] = useState("");
	const [loadingIssueId, setLoadingIssueId] = useState(null);
	const [aiSuggestions, setAiSuggestions] = useState({});

	useEffect(() => {
		const savedApiKey = localStorage.getItem("ai_api_key");
		const savedModel = localStorage.getItem("ai_model");
		if (savedApiKey) setApiKey(savedApiKey);
		if (savedModel) setSelectedModel(savedModel);
		else setSelectedModel(DEFAULT_MODEL);
	}, []);

	const saveApiKey = useCallback(() => {
		if (!tempApiKey.trim()) return false;
		setApiKey(tempApiKey);
		localStorage.setItem("ai_api_key", tempApiKey);
		setShowApiKeyInput(false);
		setTempApiKey("");
		return true;
	}, [tempApiKey]);

	const handleModelChange = useCallback((model) => {
		setSelectedModel(model);
		localStorage.setItem("ai_model", model);
	}, []);

	const askForIssue = useCallback(
		async (issue) => {
			if (!apiKey.trim()) {
				setShowApiKeyInput(true);
				return { error: "missing_api_key" };
			}
			const [providerId, modelId] = selectedModel.split(":");
			const provider = AI_PROVIDERS[providerId];
			if (!provider) return { error: "invalid_provider" };

			setLoadingIssueId(issue.id);
			const prompt = `Accessibility issue:\n${
				issue.help
			}\nHTML snippet:\n${issue.nodes
				.map((n) => n.html)
				.join("\n")}\n\nProvide WCAG-compliant resolution with code.`;
			try {
				let response;
				if (providerId === "anthropic") {
					response = await fetch(provider.endpoint, {
						method: "POST",
						headers: {
							"x-api-key": apiKey,
							"Content-Type": "application/json",
							"anthropic-version": "2023-06-01",
						},
						body: JSON.stringify({
							model: modelId,
							max_tokens: 1024,
							messages: [{ role: "user", content: prompt }],
						}),
					});
				} else if (providerId === "openai") {
					response = await fetch(provider.endpoint, {
						method: "POST",
						headers: {
							Authorization: `Bearer ${apiKey}`,
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							model: modelId,
							messages: [
								{ role: "system", content: "Accessibility expert." },
								{ role: "user", content: prompt },
							],
						}),
					});
				} else {
					response = await fetch(provider.endpoint, {
						method: "POST",
						headers: {
							Authorization: `Bearer ${apiKey}`,
							"Content-Type": "application/json",
							"HTTP-Referer": window.location.origin,
							"X-Title": "WP Accessibility Assistant",
						},
						body: JSON.stringify({
							model: modelId,
							messages: [
								{ role: "system", content: "Accessibility expert." },
								{ role: "user", content: prompt },
							],
						}),
					});
				}
				if (!response.ok) {
					return { error: await response.text() };
				}
				const data = await response.json();
				let solution =
					providerId === "anthropic"
						? data?.content?.[0]?.text
						: data?.choices?.[0]?.message?.content;
				if (solution) {
					setAiSuggestions((prev) => ({ ...prev, [issue.id]: solution }));
					return { solution };
				}
				return { error: "no_solution" };
			} catch (e) {
				return { error: e.message };
			} finally {
				setLoadingIssueId(null);
			}
		},
		[apiKey, selectedModel],
	);

	return {
		apiKey,
		selectedModel,
		showApiKeyInput,
		tempApiKey,
		setTempApiKey,
		setShowApiKeyInput,
		saveApiKey,
		handleModelChange,
		askForIssue,
		loadingIssueId,
		aiSuggestions,
	};
}
